import { DomHelper } from '../../utils';
import { UIRendererService } from '../ui';
import type { ApiError } from '../../types';

/**
 * Service for handling errors throughout the application
 * Provides centralized error processing and display functionality
 */
export class ErrorService {
  // DOM Error Display Methods for inline validation errors
  static showError(message: string): void {
    this.clearError();
    const questionContainer = DomHelper.getElement('.question-container');

    if (questionContainer) {
      const errorDiv = DomHelper.createElement('div', 'error-message', message);
      questionContainer.appendChild(errorDiv);
    }
  }

  static clearError(): void {
    DomHelper.removeElement('.error-message');
  }

  /**
   * Extract user-friendly error message from API error
   */
  static getErrorMessageFromError(error: ApiError | Error | any): string {
    if (!(error instanceof Error)) {
      return 'Failed to load survey. Please check your connection and try again.';
    }

    // Prefer API error message if present
    if ((error as any).apiResponse?.message) {
      return (error as any).apiResponse.message;
    }

    // Handle specific status codes
    const status = (error as any).status;
    switch (status) {
      case 400:
        return 'Invalid survey data. Please review your responses and try again.';
      case 403:
        return 'Survey not available.';
      case 404:
        return 'Survey not found.';
      case 409:
        return 'You have already completed this survey.';
      case 410:
        return 'Survey expired.';
      case 500:
        return 'The server is experiencing issues. Please try again later.';
      default:
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          return 'Unable to connect to the survey server. Please check your internet connection and try again.';
        }
        return 'Failed to load survey. Please check your connection and try again.';
    }
  }

  /**
   * Handle error by getting appropriate message and rendering error state
   */
  static handleError(error: any, appElement: HTMLElement): void {
    const userMessage = this.getErrorMessageFromError(error);
    const statusCode = (error as any)?.status;
    appElement.innerHTML = UIRendererService.renderErrorState(userMessage, statusCode);
  }
}